import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  getLanguageMeta,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from '../i18n/languages';

export const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: SupportedLanguage) => {
    i18n.changeLanguage(languageCode);
    handleClose();
  };

  const currentLanguageCode = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const currentLanguage = getLanguageMeta(currentLanguageCode);

  return (
    <>
      <Tooltip title={t('languageSwitcher.tooltip')}>
        <Button
          onClick={handleClick}
          color="inherit"
          size="small"
          startIcon={<LanguageIcon />}
          endIcon={<ExpandMoreIcon />}
          aria-label={t('languageSwitcher.ariaLabel')}
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            minWidth: 0,
            px: 1.25,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          {currentLanguage.shortLabel}
        </Button>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={currentLanguageCode === language.code}
          >
            <ListItemIcon>
              <span style={{ fontSize: '1.2rem' }}>{language.flag}</span>
            </ListItemIcon>
            <ListItemText
              primary={language.nativeLabel}
              secondary={
                <Typography component="span" variant="caption" color="text.secondary">
                  {language.shortLabel}
                </Typography>
              }
            />
            {currentLanguageCode === language.code && (
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

